import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Modal,
  ModalBody,
  ModalHeader,
  Button,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeviceHeartbeatService from "../../../services/DeviceHeartbeatServices/DeviceHeartbeatService";
import campaignImage from "../../../assets/images/campaign.jpg"; // your static image
// Custom hook for polling device status
const useDeviceStatus = (refreshInterval = 10000) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = useCallback(async (isInitial = false) => {
    try {
      // Only show loading during initial fetch
      if (isInitial) setLoading(true);

      const response = await DeviceHeartbeatService.getLatestDeviceStatus();
      const newData = Array.isArray(response) ? response : [];

      // Only update state if data actually changed (prevents unnecessary re-renders)
      setData((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(newData)) {
          return newData;
        }
        return prev;
      });

      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to fetch device status");
      setData([]);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch with loading state
    fetchStatus(true);

    // Set up polling interval (silent background refresh)
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(
      () => fetchStatus(false),
      refreshInterval,
    );

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchStatus, refreshInterval]);

  return { data, loading, error, refetch: () => fetchStatus(true) };
};

// Format date time nicely
const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Calculate time difference from now
const getTimeDifference = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
  if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
  return `${Math.floor(diffSecs / 86400)}d ago`;
};

// Campaign Card Component

const CampaignCard = ({ campaign, onClick }) => {
  return (
    <Card
      className="campaign-card  border-0"
      style={{
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.12)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div
        className="position-relative"
        style={{
          backgroundColor: "#f8f9fa",
          minHeight: "150px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* Always show static image */}
        <img
          src={campaignImage}
          alt={campaign.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />

        {/* Badge */}
        <div className="position-absolute top-0 end-0 p-2">
          <Badge color={campaign.isActive ? "success" : "danger"}>
            {campaign.isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>

      <CardBody className="">
        <h6
          className="mb-2 text-truncate"
          style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a" }}
        >
          {campaign.name}
        </h6>
        <p
          className="text-muted text-truncate"
          style={{ fontSize: "12px", lineHeight: "1.4" }}
        >
          {campaign.description || "No description"}
        </p>
        <div
          className="d-flex justify-content-between align-items-center text-muted"
          style={{ fontSize: "11px" }}
        >
          <span>
            <i className="ri-calendar-line "></i>
            {new Date(campaign.startDate).toLocaleDateString()}
          </span>
          <Badge
            className="tag-badge"
            style={{ backgroundColor: "#0dcaf0", marginLeft: "4px" }}
          >
            {campaign.adsCount} ads
          </Badge>
        </div>
      </CardBody>
    </Card>
  );
};

const DeviceHeartbeatStatus = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [campaignModal, setCampaignModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [selectedDeviceMacId, setSelectedDeviceMacId] = useState(null);
  const [selectedDeviceName, setSelectedDeviceName] = useState(null);
  const [reasonModal, setReasonModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const { data, loading, error, refetch } = useDeviceStatus(10000); // Refresh every 10 seconds

  // Fetch campaigns for a specific device
  const fetchCampaigns = useCallback(async (macId, machineName) => {
    try {
      setCampaignLoading(true);
      setSelectedDeviceMacId(macId);
      setSelectedDeviceName(machineName);
      const response = await DeviceHeartbeatService.getCampaignByMacId(macId);

      // Handle paginated response
      if (response && typeof response === "object") {
        setCampaigns(Array.isArray(response.data) ? response.data : []);
      } else if (Array.isArray(response)) {
        setCampaigns(response);
      } else {
        setCampaigns([]);
      }

      setCampaignModal(true);
    } catch (err) {
      toast.error(`Failed to fetch campaigns for ${macId}`);
      setCampaigns([]);
      setCampaignModal(true);
    } finally {
      setCampaignLoading(false);
    }
  }, []);

  const handleViewReason = (row) => {
    setSelectedDevice(row);
    setReasonModal(true);
  };
  // Apply client-side search
  const filteredData = useMemo(() => {
    return Array.isArray(data)
      ? data.filter((device) => {
          const searchLower = searchTerm.toLowerCase();
          return (
            (device.macId &&
              device.macId.toLowerCase().includes(searchLower)) ||
            (device.machineName &&
              device.machineName.toLowerCase().includes(searchLower)) ||
            (device.applicationName &&
              device.applicationName.toLowerCase().includes(searchLower)) ||
            (device.localIp &&
              device.localIp.toLowerCase().includes(searchLower))
          );
        })
      : [];
  }, [data, searchTerm]);

  // Custom styles for DataTable
  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
      stripedStyle: {
        backgroundColor: "#f8f9fa",
      },
    },
  };

  // Conditional row styles for green/red highlighting based on active status
  const conditionalRowStyles = [
    {
      when: (row) => row.isActive === true,
      style: {
        backgroundColor: "rgba(34, 197, 94, 0.1)",
        color: "#15803d",
        fontWeight: 500,
        borderLeft: "4px solid #22c55e",
      },
    },
    {
      when: (row) => row.isActive === false,
      style: {
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        color: "#b91c1c",
        fontWeight: 500,
        borderLeft: "4px solid #ef4444",
      },
      classNames: ["inactive-row"],
    },
  ];

  // Memoize columns to prevent DataTable from re-rendering on every state change
  const columns = useMemo(
    () => [
      {
        name: "SN",
        cell: (row, index) => <div>{index + 1}</div>,
        width: "80px",
        sortable: false,
      },
      {
        name: "MAC ID",
        selector: (row) => row.macId || "-",
        sortable: true,
        cell: (row) => (
          <code
            className=" fw-bold text-secondary"
            style={{
              cursor: "pointer",
              // textDecoration: "underline",
              transition: "color 0.2s ease",
            }}
            onClick={() => fetchCampaigns(row.macId, row.machineName)}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#0d6efd")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#0dcaf0")}
          >
            {row.macId}
          </code>
        ),
      },
      {
        name: "Machine Name",
        selector: (row) => row.machineName || "-",
        sortable: true,
      },
      {
        name: "Application",
        selector: (row) => row.applicationName || "-",
        sortable: true,
      },
      {
        name: "Version",
        selector: (row) => row.appVersion || "-",
        sortable: true,
        cell: (row) => <Badge>{row.appVersion || "-"}</Badge>,
      },
      {
        name: "Local IP",
        selector: (row) => row.localIp || "-",
        sortable: true,
        cell: (row) => <code>{row.localIp || "-"}</code>,
      },
      {
        name: "Last Heartbeat",
        selector: (row) => formatDateTime(row.heartbeatAt || row.lastHeartbeat),
        sortable: true,
        cell: (row) => (
          <div>
            <div>{formatDateTime(row.heartbeatAt || row.lastHeartbeat)}</div>
            <small className="text-muted">
              {getTimeDifference(row.heartbeatAt || row.lastHeartbeat)}
            </small>
          </div>
        ),
      },
      {
        name: "Status",
        selector: (row) => (row.isActive ? "Active" : "Inactive"),
        sortable: true,
        cell: (row) => (
          <Badge color={row.isActive ? "success" : "danger"}>
            {row.isActive ? (
              <>
                <i className="ri-checkbox-circle-fill"></i> Active
              </>
            ) : (
              <>
                <i className="ri-close-circle-fill"></i> Inactive
              </>
            )}
          </Badge>
        ),
      },

      {
        name: "Action",
        cell: (row) => (
          <div className="d-flex gap-2">
            <Button
              color="soft-info"
              className="btn-sm"
              onClick={() => handleViewReason(row)}
              title="View Reason"
              disabled={row.isActive} // disable for active devices
            >
              <i className="ri-eye-line" />
            </Button>
          </div>
        ),
        center: true,
        width: "120px",
      },
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <BreadCrumb
            title="Device Heartbeat Status"
            pageTitle="Device Status"
          />

          <Row>
            <Col lg="12">
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Device Status Overview</h4>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => navigate("/device-heartbeat-logs")}
                    >
                      <i className="ri-file-list-line me-1"></i>
                      Logs
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => refetch()}
                      disabled={loading}
                    >
                      <i className="ri-refresh-line me-1"></i>
                      {loading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                </CardHeader>

                {/* Search Bar */}
                <CardBody className="pb-2">
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by MAC ID, Machine Name, Application, or IP..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                      }}
                    />
                  </div>

                  {/* Loading State */}
                  {loading && data.length === 0 && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <Triangle
                        height="80"
                        width="80"
                        color="#0dcaf0"
                        ariaLabel="triangle-loading"
                        visible={true}
                      />
                    </div>
                  )}

                  {/* Error State */}
                  {error && !loading && (
                    <div className="alert alert-danger mt-3">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </div>
                  )}

                  {/* No Data State */}
                  {!loading && filteredData.length === 0 && searchTerm && (
                    <div className="alert alert-info mt-3">
                      <i className="ri-information-line me-2"></i>
                      No devices found matching your search criteria.
                    </div>
                  )}

                  {/* DataTable */}
                  {!loading && filteredData.length > 0 && (
                    <DataTable
                      columns={columns}
                      data={filteredData}
                      pagination
                      persistTableHead
                      conditionalRowStyles={conditionalRowStyles}
                      customStyles={customStyles}
                      striped
                      responsive
                      selectableRows={false}
                      onRowMouseEnter={(row, e) => {
                        if (!row.isActive && row.reason) {
                          e.currentTarget.setAttribute("title", row.reason);
                        }
                      }}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={reasonModal}
            toggle={() => setReasonModal(false)}
            centered
          >
            <ModalHeader toggle={() => setReasonModal(false)}>
              Device Status Details
            </ModalHeader>
            <ModalBody>
              {selectedDevice && (
                <>
                  <p>
                    <strong>Reason:</strong>
                  </p>
                  <div className="alert alert-danger mb-0">
                    {selectedDevice.reason || "No reason provided"}
                  </div>
                </>
              )}
            </ModalBody>
          </Modal>
        </Container>
      </div>

      {/* Campaign Modal */}
      <Modal
        isOpen={campaignModal}
        toggle={() => setCampaignModal(false)}
        size="xl"
        centered
        contentClassName="border-0 shadow-lg"
      >
        <ModalHeader
          className="bg-light border-bottom"
          toggle={() => setCampaignModal(false)}
          style={{ padding: "20px 24px" }}
        >
          <div className="d-flex align-items-center gap-2">
            <i
              className="ri-campaign-line"
              style={{ fontSize: "20px", color: "#0dcaf0" }}
            ></i>
            <div>
              <h6 className="mb-0">Campaigns for Device</h6>
              <small className="text-muted">
                {selectedDeviceName} • MAC: <code>{selectedDeviceMacId}</code>
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "24px" }}>
          {campaignLoading && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Triangle
                height="80"
                width="80"
                color="#0dcaf0"
                ariaLabel="triangle-loading"
                visible={true}
              />
            </div>
          )}

          {!campaignLoading && campaigns.length === 0 && (
            <div className="alert alert-info" role="alert">
              <i className="ri-information-line me-2"></i>
              No campaigns found for this device.
            </div>
          )}

          {!campaignLoading && campaigns.length > 0 && (
            <Row className="g-3">
              {campaigns.map((campaign) => (
                <Col key={campaign.id} xs="12" md="5" lg="3">
                  <CampaignCard
                    campaign={campaign}
                    onClick={() => {
                      // You can add navigation or detailed view here
                      toast.success(`Campaign: ${campaign.name}`);
                    }}
                  />
                </Col>
              ))}
            </Row>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default DeviceHeartbeatStatus;
